import type { NavigateFunction } from 'react-router-dom'
import { setPreviousUrl } from './helpers'

export const CAMPAIGN_SIGNUP_REDIRECT_KEY = 'campaign_signup_redirect'
export const CAMPAIGN_SIGNUP_CONTEXT_KEY = 'campaign_signup_context'
export const CAMPAIGN_OPPORTUNITY_PARAM = 'opportunity'
export const ACCEPT_NDA_ROUTE = '/accept-NDA'

export type CampaignSignupRedirect = {
  opportunity_slug: string
  opportunity_title: string
  opportunity_id: number
  status: string
  access_token?: string | null
}

const normalizeOpportunityPathname = (pathname: string): string =>
  pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname

const OPPORTUNITY_LINK_PATH = /^\/opportunity\/link\/([^/]+)$/
const OPPORTUNITY_STANDARD_PATH = /^\/opportunity\/([^/]+)\/(\d+)$/

const isValidStoredCampaignRedirect = (parsed: CampaignSignupRedirect): boolean => {
  if (!parsed || typeof parsed !== 'object' || !parsed.opportunity_slug) {
    return false
  }

  if (parsed.status === 'LINK_ONLY' && parsed.access_token) {
    return true
  }

  return Boolean(parsed.opportunity_id)
}

export const isOpportunityDeepLinkPath = (pathname: string): boolean => {
  const normalized = normalizeOpportunityPathname(pathname)
  return OPPORTUNITY_LINK_PATH.test(normalized) || OPPORTUNITY_STANDARD_PATH.test(normalized)
}

export const buildCampaignSignupRedirectFromOpportunity = (
  projectSlug: string,
  id: number,
  title?: string,
): CampaignSignupRedirect => ({
  opportunity_slug: projectSlug,
  opportunity_title: title?.trim() || projectSlug,
  opportunity_id: id,
  status: 'PUBLISHED',
})

export const parseOpportunityRedirectFromLocation = (
  pathname: string,
  search: string,
): CampaignSignupRedirect | null => {
  const normalized = normalizeOpportunityPathname(pathname)

  const linkMatch = normalized.match(OPPORTUNITY_LINK_PATH)
  if (linkMatch) {
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
    const accessToken = params.get('access')?.trim()
    if (!accessToken) return null

    const slug = linkMatch[1]
    return {
      opportunity_slug: slug,
      opportunity_title: slug,
      opportunity_id: 0,
      status: 'LINK_ONLY',
      access_token: accessToken,
    }
  }

  const standardMatch = normalized.match(OPPORTUNITY_STANDARD_PATH)
  if (standardMatch) {
    const slug = standardMatch[1]
    const id = Number(standardMatch[2])
    if (!slug || !Number.isFinite(id)) return null

    return {
      opportunity_slug: slug,
      opportunity_title: slug,
      opportunity_id: id,
      status: 'PUBLISHED',
    }
  }

  return null
}

export const storeOpportunityRedirectFromLocation = (pathname: string, search: string): boolean => {
  const redirect = parseOpportunityRedirectFromLocation(pathname, search)
  if (!redirect) return false

  storeCampaignSignupRedirect(redirect)
  return true
}

export const resolveCampaignOpportunitySlug = (
  pathParam: string | undefined,
  search: string,
): string | undefined => {
  if (pathParam && !pathParam.includes('@')) {
    return pathParam
  }

  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
  const fromQuery = params.get(CAMPAIGN_OPPORTUNITY_PARAM)?.trim()
  return fromQuery || undefined
}

export const buildCampaignOpportunityPath = (data: CampaignSignupRedirect): string => {
  if (data.status === 'LINK_ONLY' && data.access_token) {
    return `/opportunity/link/${data.opportunity_slug}?access=${data.access_token}`
  }

  return `/opportunity/${data.opportunity_slug}/${data.opportunity_id}`
}

const requiresNdaForCampaignView = (redirect: CampaignSignupRedirect): boolean => {
  return !(redirect.status === 'LINK_ONLY' && redirect.access_token)
}

export const storeCampaignSignupRedirect = (data: CampaignSignupRedirect): void => {
  localStorage.setItem(CAMPAIGN_SIGNUP_REDIRECT_KEY, JSON.stringify(data))
}

export const getStoredCampaignSignupRedirect = (): CampaignSignupRedirect | null => {
  const raw = localStorage.getItem(CAMPAIGN_SIGNUP_REDIRECT_KEY)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as CampaignSignupRedirect
    if (!isValidStoredCampaignRedirect(parsed)) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export const clearCampaignSignupRedirect = (): void => {
  localStorage.removeItem(CAMPAIGN_SIGNUP_REDIRECT_KEY)
}

export const storeCampaignSignupContext = (opportunityTitle: string): void => {
  const title = opportunityTitle.trim()
  if (title) {
    localStorage.setItem(CAMPAIGN_SIGNUP_CONTEXT_KEY, title)
  }
}

export const getCampaignSignupContext = (): string | null => {
  const title = localStorage.getItem(CAMPAIGN_SIGNUP_CONTEXT_KEY)?.trim()
  return title || null
}

export const clearCampaignSignupContext = (): void => {
  localStorage.removeItem(CAMPAIGN_SIGNUP_CONTEXT_KEY)
}

export const resolvePostAuthCampaignDestination = (acceptedNDA: boolean): string | null => {
  const redirect = getStoredCampaignSignupRedirect()
  if (!redirect) return null

  const opportunityPath = buildCampaignOpportunityPath(redirect)
  setPreviousUrl(opportunityPath)
  clearCampaignSignupRedirect()

  if (requiresNdaForCampaignView(redirect) && !acceptedNDA) {
    if (redirect.opportunity_title) {
      storeCampaignSignupContext(redirect.opportunity_title)
    }
    return ACCEPT_NDA_ROUTE
  }

  clearCampaignSignupContext()
  return opportunityPath
}

export const consumeCampaignSignupRedirectPath = (): string | null => {
  return resolvePostAuthCampaignDestination(true)
}

export const navigateToCampaignOpportunity = (
  navigate: NavigateFunction,
  data: CampaignSignupRedirect,
): void => {
  const path = buildCampaignOpportunityPath(data)
  setPreviousUrl(path)
  navigate(path)
  clearCampaignSignupRedirect()
}

export const navigateToStoredCampaignOpportunity = (
  navigate: NavigateFunction,
  acceptedNDA: boolean,
): boolean => {
  const destination = resolvePostAuthCampaignDestination(acceptedNDA)
  if (!destination) return false

  navigate(destination)
  return true
}
